// Shared category configuration for markets pages
// Used by [category]/page.tsx, [category]/[subcategory]/page.tsx, and sitemap-categories.xml

export const BACKEND_URL = process.env.BACKEND_URL || "http://localhost:4000";

// Gamma API (Polymarket) — used directly from SSR pages for tag-based market queries
// These don't go through the backend (which has no /api/polymarket/markets route)
export const GAMMA_EVENTS_API = "https://gamma-api.polymarket.com/events";

// Category to Polymarket tag slug mapping
//
// `weather` is canonical — Polymarket consolidated `/climate-science`
// and `/weather-science` to `/weather` (both redirect 308 → /weather).
// We keep the old slugs out of CATEGORY_TAG_MAP so the sidebar /
// taxonomy doesn't render duplicate "Climate" / "Weather" tabs; the
// Next.js redirects (next.config.ts) translate any stale links.
export const CATEGORY_TAG_MAP: Record<string, string> = {
  crypto: "crypto",
  economy: "economy",
  politics: "politics",
  elections: "world-elections",
  geopolitics: "geopolitics",
  culture: "pop-culture",
  weather: "weather",
  world: "world",
  elon: "elon-musk",
  finance: "finance",
  tech: "tech",
  sports: "sports",
  esports: "esports",
  // World Cup as a top-level category → Polymarket FIFA World Cup tag.
  // All data sourced from Polymarket gamma (markets, moneyline games,
  // futures), NOT our ESPN sports-live feed. 2026-06-10.
  "world-cup": "fifa-world-cup",
  iran: "iran",
  // Elections subcategories (have their own URL pages)
  macro: "macro-single",
  "fed-rates": "fed-rates",
  trump: "trump-presidency",
};

// Known categories (tag-based + special) — used as base set
export const VALID_CATEGORIES = new Set([
  ...Object.keys(CATEGORY_TAG_MAP),
  "trending",
  "new",
  "breaking",
  "ending-soon",
  "for-you",
  "mentions",
  "bookmarks",
]);

// Dynamic validation: accept known categories + any valid-looking slug (for dynamic Polymarket categories)
const VALID_SLUG_PATTERN = /^[a-z][a-z0-9-]*$/;
export function isValidCategory(slug: string): boolean {
  return VALID_CATEGORIES.has(slug) || VALID_SLUG_PATTERN.test(slug);
}

// Resolve tag slug for a category — known mapping or use slug directly as fallback
export function resolveTagSlug(category: string): string | null {
  return CATEGORY_TAG_MAP[category] || (VALID_SLUG_PATTERN.test(category) ? category : null);
}

// Categories that should NOT have SSR data (personalized or special rendering)
export const SKIP_SSR_CATEGORIES = new Set(["for-you", "mentions", "bookmarks", "ending-soon"]);

// Categories that should NOT appear in sitemaps (auth-required or non-indexable)
export const NO_SITEMAP_CATEGORIES = new Set(["for-you", "bookmarks"]);

// Rich SEO data per category
export const CATEGORY_SEO: Record<string, {
  image: string;
  title: string;
  description: string;
  ssrHeading: string;
  ssrParagraph: string;
}> = {
  "world-cup": {
    image: "fifa.webp",
    title: "World Cup Prediction Markets",
    description: "FIFA World Cup prediction markets — match winners, group stage, knockout bracket, Golden Boot and tournament winner odds. Real-time odds powered by Polymarket on FrenFlow.",
    ssrHeading: "World Cup Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on FIFA World Cup prediction markets covering match results, group standings, the knockout bracket, Golden Boot and tournament winner. Real-time odds and deep liquidity powered by Polymarket.",
  },
  politics: {
    image: "og-politics.webp",
    title: "Politics Prediction Markets",
    description: "Trade on political prediction markets with real money. US elections, congressional legislation, executive orders, Supreme Court decisions and global policy. Real-time odds powered by Polymarket.",
    ssrHeading: "Politics Prediction Markets on FrenFlow",
    ssrParagraph: "Browse and trade political prediction markets covering US elections, congressional legislation, executive orders, Supreme Court decisions and global policy. Get real-time odds and deep liquidity powered by Polymarket.",
  },
  sports: {
    image: "og-sports.webp",
    title: "Sports Prediction Markets",
    description: "Sports prediction markets — trade on NFL, NBA, Premier League, La Liga, Champions League, UFC, F1 and more. Real-time odds, deep liquidity. Trade outcomes with real money on FrenFlow.",
    ssrHeading: "Sports Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on sports prediction markets for NFL, NBA, Premier League, La Liga, Champions League, UFC, Formula 1 and more. Real-time odds and deep liquidity powered by Polymarket.",
  },
  esports: {
    image: "og-sports.webp",
    title: "Esports Prediction Markets",
    description: "Esports prediction markets — trade on CS2, League of Legends, Dota 2, Valorant, Overwatch and more. Real-time odds, deep liquidity. Trade outcomes with real money on FrenFlow.",
    ssrHeading: "Esports Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on esports prediction markets for CS2, League of Legends, Dota 2, Valorant, Overwatch, Call of Duty and more. Real-time odds and deep liquidity powered by Polymarket.",
  },
  crypto: {
    image: "og-crypto.webp",
    title: "Crypto Prediction Markets",
    description: "Crypto prediction markets — Bitcoin price targets, Ethereum milestones, altcoin events, DeFi outcomes and regulatory decisions. Live odds powered by Polymarket on FrenFlow.",
    ssrHeading: "Crypto Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on crypto prediction markets covering Bitcoin price targets, Ethereum milestones, altcoin events, DeFi protocol outcomes and regulatory decisions. Live odds powered by Polymarket.",
  },
  finance: {
    image: "og-finance.webp",
    title: "Finance Prediction Markets",
    description: "Financial prediction markets — Federal Reserve rate decisions, stock market milestones, IPO outcomes, earnings surprises and macroeconomic indicators. Trade with real money on FrenFlow.",
    ssrHeading: "Finance Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on financial prediction markets covering Federal Reserve rate decisions, stock market milestones, IPO outcomes, earnings surprises and macroeconomic indicators powered by Polymarket.",
  },
  elections: {
    image: "og-elections.webp",
    title: "Elections Prediction Markets",
    description: "Election prediction markets — US presidential, congressional, gubernatorial races and global elections. Real-time polling odds, voter sentiment and outcome probabilities on FrenFlow.",
    ssrHeading: "Elections Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on election prediction markets covering US presidential, congressional, gubernatorial races and elections worldwide. Real-time polling odds and outcome probabilities powered by Polymarket.",
  },
  geopolitics: {
    image: "og-geopolitics.webp",
    title: "Geopolitics Prediction Markets",
    description: "Geopolitical prediction markets — international conflicts, trade agreements, sanctions, NATO decisions, UN resolutions and diplomatic negotiations. Real-time odds on FrenFlow.",
    ssrHeading: "Geopolitics Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on geopolitical prediction markets covering international conflicts, trade agreements, sanctions, NATO decisions, UN resolutions and diplomatic negotiations. Real-time odds powered by Polymarket.",
  },
  economy: {
    image: "og-economy.webp",
    title: "Economy Prediction Markets",
    description: "Economic prediction markets — GDP growth, inflation rates, unemployment data, consumer confidence, housing market trends and central bank policy. Trade real money on FrenFlow.",
    ssrHeading: "Economy Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on economic prediction markets covering GDP growth, inflation rates, unemployment data, consumer confidence, housing market trends and central bank policy decisions powered by Polymarket.",
  },
  tech: {
    image: "og-tech.webp",
    title: "Tech Prediction Markets",
    description: "Technology prediction markets — AI breakthroughs, product launches, startup acquisitions, app store rankings, chip wars and Big Tech earnings. Live odds on FrenFlow.",
    ssrHeading: "Tech Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on technology prediction markets covering AI breakthroughs, product launches, startup acquisitions, app store rankings, semiconductor competition and Big Tech earnings powered by Polymarket.",
  },
  culture: {
    image: "og-culture.webp",
    title: "Culture & Entertainment Prediction Markets",
    description: "Pop culture prediction markets — Oscars, Grammys, box office results, TV show renewals, celebrity events, viral moments and entertainment industry outcomes on FrenFlow.",
    ssrHeading: "Culture & Entertainment Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on pop culture prediction markets covering Oscars, Grammys, box office results, TV show renewals, celebrity events, viral moments and entertainment industry outcomes powered by Polymarket.",
  },
  world: {
    image: "og-world.webp",
    title: "World News Prediction Markets",
    description: "World news prediction markets — global events, humanitarian crises, climate summits, international treaties, space exploration milestones and cross-border developments on FrenFlow.",
    ssrHeading: "World News Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on world news prediction markets covering global events, humanitarian crises, climate summits, international treaties, space exploration milestones and cross-border developments powered by Polymarket.",
  },
  elon: {
    image: "og-elon-musk.webp",
    title: "Elon Musk Prediction Markets",
    description: "Elon Musk prediction markets — Tesla stock targets, SpaceX launches, X platform changes, Neuralink milestones, DOGE department decisions and Musk tweet outcomes on FrenFlow.",
    ssrHeading: "Elon Musk Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on Elon Musk prediction markets covering Tesla stock targets, SpaceX launches, X platform changes, Neuralink milestones, DOGE department decisions and Musk-related events powered by Polymarket.",
  },
  weather: {
    image: "og-climate.webp",
    title: "Weather Prediction Markets",
    description: "Weather prediction markets — daily temperature forecasts, hurricanes, snow, rain totals and climate records on FrenFlow.",
    ssrHeading: "Weather Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on weather prediction markets covering daily temperature forecasts, hurricanes, snow, rain totals and climate records powered by Polymarket.",
  },
  mentions: {
    image: "og-mentions.webp",
    title: "Mentions Prediction Markets",
    description: "Prediction markets about social media mentions, viral content and trending topics. Track what the internet is talking about and trade on outcomes with real money on FrenFlow.",
    ssrHeading: "Mentions Prediction Markets on FrenFlow",
    ssrParagraph: "Discover prediction markets about social media mentions, viral content and trending topics. Track what the internet is talking about and trade on outcomes powered by Polymarket.",
  },
  macro: {
    image: "og-economy.webp",
    title: "Macro Prediction Markets",
    description: "Macroeconomic prediction markets — GDP forecasts, trade balances, consumer spending, manufacturing indices and global economic outlook. Trade with real money on FrenFlow.",
    ssrHeading: "Macro Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on macroeconomic prediction markets covering GDP forecasts, trade balances, consumer spending, manufacturing indices and global economic outlook powered by Polymarket.",
  },
  "fed-rates": {
    image: "og-finance.webp",
    title: "Fed Rates Prediction Markets",
    description: "Federal Reserve interest rate prediction markets — FOMC meeting outcomes, rate cuts, rate hikes, dot plot projections and monetary policy decisions. Live odds on FrenFlow.",
    ssrHeading: "Fed Rates Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on Federal Reserve interest rate prediction markets covering FOMC meeting outcomes, rate decisions, dot plot projections and monetary policy. Live odds powered by Polymarket.",
  },
  trump: {
    image: "og-politics.webp",
    title: "Trump Prediction Markets",
    description: "Trump presidency prediction markets — executive orders, cabinet appointments, policy decisions, legal proceedings and political events. Real-time odds on FrenFlow.",
    ssrHeading: "Trump Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on Trump presidency prediction markets covering executive orders, cabinet appointments, policy decisions, legal proceedings and political events. Real-time odds powered by Polymarket.",
  },
  iran: {
    image: "og-iran.webp",
    title: "Iran Prediction Markets",
    description: "Iran prediction markets — nuclear negotiations, sanctions, geopolitical tensions, regime developments, oil production targets and diplomatic outcomes. Real-time odds on FrenFlow.",
    ssrHeading: "Iran Prediction Markets on FrenFlow",
    ssrParagraph: "Trade on Iran prediction markets covering nuclear negotiations, sanctions, geopolitical tensions, regime developments, oil production and diplomatic outcomes. Real-time odds powered by Polymarket.",
  },
};

// Helper to format label from slug
const CATEGORY_LABELS: Record<string, string> = {
  "weather-science": "Weather & Science",
  "climate-science": "Weather & Science",
};

export function categoryLabel(slug: string): string {
  return CATEGORY_LABELS[slug] || slug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// Helper to format count for display (e.g. 1500 → "1k+")
export function formatCount(n: number): string {
  if (n >= 1000) return `${Math.floor(n / 1000)}k+`;
  return String(n);
}
